import type { Maybe } from '@amazon/vinyl-util'
/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Pure id derivation shared by every detail-page href builder. Lives in `util/`
 * (a biref-safe leaf) so builders in `playback/`, `search/`, `track/` and
 * `blackbolt/` can all reach the same ASIN-preferring logic without importing
 * across feature boundaries.
 */

/**
 * Pulls the bare id out of an entity URI — whatever follows the last `:` after
 * dropping a `?…` query fragment. Handles two live wire shapes:
 *
 * 1. **Firefly URIs** — `amzn1.mp3d.album:B0XXXX` → `B0XXXX`.
 * 2. **MRNs** — `mrn:1.0:catalog:album:asin:B0GVN6XSJC?territory=US` →
 *    `B0GVN6XSJC`.
 *
 * `null` for missing / empty / delimiter-less / empty-tail input, so callers
 * can fall through to another id source. Values that might already be bare
 * (e.g. `playParams.id`) should use `sanitizeId` — it passes them untouched.
 */
export function extractId(uri: Maybe<string>): string | null {
    if (!uri) return null
    // Strip the query fragment first — MRNs put `?territory=US` AFTER the id,
    // so `lastIndexOf(':')` on the full string would leave it on the tail.
    const qIdx = uri.indexOf('?')
    const noQuery = qIdx >= 0 ? uri.slice(0, qIdx) : uri
    const idx = noQuery.lastIndexOf(':')
    if (idx < 0) return null
    const tail = noQuery.slice(idx + 1)
    return tail.length > 0 ? tail : null
}

/**
 * Normalizes a `playParams.id` / `playParams.containerId` into a bare route
 * id. Rewind's wire response is inconsistent: usually already bare (`B0XXXX`,
 * `AR55`), but some feeds echo the full MRN into the same slot. Strips the
 * wrapper and query suffix when present, passes bare ids through untouched.
 * `null` for missing / empty / empty-tail input.
 */
export function sanitizeId(raw: Maybe<string>): string | null {
    if (!raw) return null
    // Drop the query fragment first — defense-in-depth even for bare ids that
    // somehow still carry one.
    const qIdx = raw.indexOf('?')
    const noQuery = qIdx >= 0 ? raw.slice(0, qIdx) : raw
    // No delimiter → already bare; return if non-empty.
    const idx = noQuery.lastIndexOf(':')
    if (idx < 0) return noQuery.length > 0 ? noQuery : null
    const tail = noQuery.slice(idx + 1)
    return tail.length > 0 ? tail : null
}

/**
 * The minimal shape every track-bearing wire entity exposes: a bare `playParams.id`
 * (the ASIN), a signed-in `uri` (an asin-MRN), and the GraphQL `id` (which is the
 * catalog MRN for a signed-in track, or a bare id otherwise).
 */
export interface TrackIdSource {
    readonly id?: Maybe<string>
    readonly uri?: Maybe<string>
    readonly mediaType?: Maybe<string>
    readonly playParams?: Maybe<{ readonly id?: Maybe<string> }>
}

/**
 * The route id for a track's `/tracks/:id` detail link. Prefers the ASIN
 * (`playParams.id`, else the asin pulled from the signed-in MRN `uri`) and
 * falls back to the raw `id` only when no ASIN is derivable — that raw value
 * may be an MRN, which the track route resolver still accepts, so an
 * asin-less entity keeps a working (if MRN) link. `null` when nothing resolves.
 */
export function trackRouteId(src: TrackIdSource): string | null {
    return sanitizeId(src.playParams?.id) ?? extractId(src.uri) ?? src.id ?? null
}
